import { Component, EventEmitter, ElementRef, Inject, Input, OnInit, Output } from '@angular/core';
import * as ckEditor from '../../standalone-packages/ckeditor-5-custom-build/build/ckeditor';
import { debounceTime, distinctUntilChanged, Observable, Subject, takeUntil } from 'rxjs';
import { SpellCheckerService } from '../../services/spell-checker.service';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from '../../services/common.service';
import { SCConfig } from '../../../sc-config/constant/sc-config.constant';
import Editor from '../../standalone-packages/ckeditor-5-custom-build/build/ckeditor';
import { Store } from '@ngrx/store';
import { getCurrentLanguage, State } from '../../standalone-packages/ecap3/core';
import { ToastMessageService } from '../../services/toast-message.service';
import { AbstractControl, ValidatorFn } from '@angular/forms';

@Component({
    selector: 'app-basic-ckeditor',
    templateUrl: './basic-ckeditor.component.html',
    styleUrls: ['./basic-ckeditor.component.scss'],
    standalone: false
})
export class BasicCkeditorComponent implements OnInit {
    @Input() ckEditorContent: string;
    @Input() ckEditorConfig: any;
    @Input() isColorDisable: boolean = false;
    @Input() questionId: string = '';
    @Input() caseId: string = '';
    @Input() enableAiButton: boolean = false;
    @Input() disableCkEditor: boolean = false;
    @Input() source!: string;
    @Input() lineAndCharConfig: any = {};
    @Input() isReadOnly: boolean = false;
    @Input() customFormControl: AbstractControl | null = null;
    @Input() statLineCount: AbstractControl | null = null;

    // output events
    @Output() onCursorChange: EventEmitter<any> = new EventEmitter(null);
    @Output() editorChange: EventEmitter<any> = new EventEmitter(null);

    // event and plugin variables
    readonly ipexAiUndo: string = 'ipexAiUndo';
    readonly ipexAiLoading: string = 'ipexAiLoading';
    readonly undo: string = 'Undo';
    readonly ipexAiButton: string = 'aiButton';
    readonly allowedLength: number = 3000;
    readonly lineAndCharExceededEvent: string = 'lineAndCharExceeded';
    readonly blockQuote: string = 'blockQuote';
    readonly lineNumberAndCharCounterIndicator: string = 'lineNumberAndCharCounterIndicator';

    // local variables
    lang$: Observable<string>;
    private readonly destroy$: Subject<boolean> = new Subject<boolean>();
    public classicEditor: any = ckEditor.default;
    classicEditorItemsConfig: any;

    private editorInstance: any = null;
    private readonly errorMessage$: Subject<string> = new Subject<string>();

    // tooltip variables
    public hasLineLimitExceededError: boolean = false;
    private tooltipValidator: ValidatorFn | null = null;

    constructor(
        private readonly spellCheckerService: SpellCheckerService,
        private readonly translateService: TranslateService,
        private readonly commonService: CommonService,
        @Inject(Store) private readonly store: Store<State>,
        private readonly elementRef: ElementRef,
        private readonly toastMessageService: ToastMessageService
    ) {
        this.classicEditorItemsConfig = {
            toolbar: {
                items: this.classicEditor.defaultConfig.toolbar.items.filter(
                    item =>
                        item !== this.blockQuote && // if we remove the feature from the build, previous blockquote style persist
                        item !== this.ipexAiButton &&
                        item !== this.ipexAiUndo &&
                        item !== this.ipexAiLoading &&
                        item !== this.lineNumberAndCharCounterIndicator
                )
            }
        };
    }

    ngOnInit(): void {
        this.setupErrorMessageSubscription();
        // For formatted wrap preview
        if (this.enableAiButton) {
            this.classicEditorItemsConfig.toolbar.items.push(this.ipexAiUndo);
            this.classicEditorItemsConfig.toolbar.items.push(this.ipexAiButton);
            this.classicEditorItemsConfig.toolbar.items.push(this.ipexAiLoading);
        }

        // we need to show this in the last of the toolbar items
        // can be null or empty object
        if (this.hasLineAndCharLimitConfig()) {
            this.classicEditorItemsConfig.toolbar.items.push(this.lineNumberAndCharCounterIndicator);

            // subscribing for regulation recommendation editor config changes
            if (this.source === 'REGULATION_RECOMMENDATION_EDITOR') {
                this.subscribeForRegulationConfigChanges();
            }

            this.setConfigForLineAndCharIndicator(this.lineAndCharConfig);
            this.updateEditorSize(
                this.lineAndCharConfig?.maxCharCount,
                this.lineAndCharConfig?.maxLineCount,
                this.lineAndCharConfig?.baseHeightLineCount
            );
        }

        if (this.disableCkEditor) {
            this.isReadOnly = this.disableCkEditor;
            this.classicEditorItemsConfig.toolbar.items = [];
        }
    }

    private setConfigForLineAndCharIndicator(lineAndCharConfig: any): void {
        // pushing the configs for line number and char counter indicator
        this.classicEditorItemsConfig.lineNumberAndCharCounterConfig = {
            maxCharCount: lineAndCharConfig?.maxCharCount ?? -1,
            maxLineCount: lineAndCharConfig?.maxLineCount ?? -1,
            maxBulletCharCount: lineAndCharConfig?.maxBulletCharCount ?? 95
        };
    }

    // This function is used to update the size of the editor based on the maxCharCount and maxLineCount
    private updateEditorSize(maxCharCount: number, maxLineCount: number, baseHeightLineCount: number): void {
        // using setTimeout to ensure the view is initialized

        const hostElement = this.elementRef?.nativeElement;
        if (!hostElement) {
            return;
        }

        // set the class to the host element to indicate fixed sizing mode
        if (hostElement.classList.contains('ckeditor-fixed-size')) {
            hostElement.classList.remove('ckeditor-fixed-size');
        }

        hostElement.classList.add('ckeditor-fixed-size');

        // set the width of the editor based on the maxCharCount
        const isWindows = this.isWindows();
        const characterHeightAndWidth = this.measureCharacterHeightAndWidth();

        // in mac/linux, we need to subtract 1 character widths from the width
        const widthAdjustment = isWindows ? 0 : characterHeightAndWidth.width;

        // When scrollbar is hidden (baseHeightLineCount > 0),
        // we need to subtract extra width because of hidden scrollbar
        let scrollbarWidth = 0;
        if (baseHeightLineCount > 0) {
            scrollbarWidth = characterHeightAndWidth.width;
        }

        const width = maxCharCount * characterHeightAndWidth.width - widthAdjustment - scrollbarWidth;
        hostElement.style.setProperty('--ckeditor-width', `${width}px`);

        // Calculate heights for auto-growing editor
        const lineHeight = characterHeightAndWidth.height;
        const toolbarHeight = 50;
        const padding = 30;
        let minHeight = maxLineCount * lineHeight + toolbarHeight + padding;

        hostElement.style.removeProperty('--ckeditor-max-height');

        if (hostElement.classList.contains('ckeditor-hide-scrollbar')) {
            hostElement.classList.remove('ckeditor-hide-scrollbar');
        }

        if (baseHeightLineCount > 0) {
            // Apply hidden scrollbar class for when base grow height is applicable
            hostElement.classList.add('ckeditor-hide-scrollbar');
            minHeight = baseHeightLineCount * lineHeight + toolbarHeight + padding;

            // we need to set the max height to the base grow height
            hostElement.style.setProperty('--ckeditor-max-height', `${minHeight}px`);
        }

        // Minimum height
        hostElement.style.setProperty('--ckeditor-min-height', `${minHeight}px`);

        // Initial height (starts at minimum)
        hostElement.style.setProperty('--ckeditor-height', `${minHeight}px`);
    }

    // Detect if the user is on Windows
    private isWindows(): boolean {
        if (typeof navigator === 'undefined') {
            return false;
        }
        const platform = navigator.platform?.toLowerCase() || '';
        const userAgent = navigator.userAgent?.toLowerCase() || '';

        // Check for Windows indicators
        return (
            platform.includes('win') ||
            userAgent.includes('windows') ||
            userAgent.includes('win32') ||
            userAgent.includes('win64') ||
            userAgent.includes('wow64')
        );
    }

    private measureCharacterHeightAndWidth(): { width: number; height: number } {
        // Create a temporary element with the exact same font settings
        const measureElement = document.createElement('span');
        measureElement.style.position = 'absolute';
        measureElement.style.visibility = 'hidden';
        measureElement.style.whiteSpace = 'nowrap';
        measureElement.style.fontFamily = 'monospace';
        measureElement.style.fontSize = '16px'; // as ckeditor has 16px font size by default
        measureElement.textContent = '0'; // Use '0' as a standard character

        document.body.appendChild(measureElement);
        const width = measureElement.offsetWidth;
        const height = measureElement.offsetHeight;
        document.body.removeChild(measureElement);

        return { width, height };
    }

    private setupErrorMessageSubscription(): void {
        this.errorMessage$.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe(message => {
            if (message) {
                this.toastMessageService.error(this.translateService.instant(message), 4000);
            }
        });
    }

    private toggleEditorOverflow(isReadonly: boolean): void {
        if (this.hasLineAndCharLimitConfig()) {
            return;
        }

        const element: any = document.querySelectorAll('.ck.ck-editor__main');
        for (let item of element) {
            item.style.overflow = isReadonly ? 'scroll' : 'hidden';
        }
    }

    public onChange(event: any): void {
        const { editor } = event;
        let data = editor.getData();
        
        const trimmed = data.trim();
        if (
            trimmed === '' ||
            trimmed === '<p></p>' ||
            trimmed === '<p>&nbsp;</p>' ||
            trimmed === '<p><br></p>' ||
            trimmed === '<p><br data-cke-filler="true"></p>' ||
            trimmed === '<br>'
        ) {
            data = '';
        }

        this.editorChange.emit(data);
        this.updateAiButtonTooltip(editor);
    }

    private updateFormControlValidation(data: any): void {
        if (!this.customFormControl) {
            return;
        }

        if (data) {
            this.hasLineLimitExceededError = data?.hasValidationError ?? false;
        }

        if (this.hasLineLimitExceededError) {
            // Create tooltip validator if it doesn't exist
            if (!this.tooltipValidator) {
                this.tooltipValidator = (_: AbstractControl) => {
                    return this.hasLineLimitExceededError
                        ? { tooltipError: { message: 'Line number exceeded the maximum limit' } }
                        : null;
                };
            }

            // Add tooltip validator if not already added
            if (this.customFormControl instanceof AbstractControl && 'addValidators' in this.customFormControl) {
                (this.customFormControl as any).addValidators(this.tooltipValidator);
            }
        } else {
            // Remove tooltip validator if it exists
            if (
                this.tooltipValidator &&
                this.customFormControl instanceof AbstractControl &&
                'removeValidators' in this.customFormControl
            ) {
                (this.customFormControl as any).removeValidators(this.tooltipValidator);
            }
        }

        // Trigger validation update
        this.customFormControl.updateValueAndValidity();
    }

    public onFocus(event: any): void {
        this.toggleEditorOverflow(true);
    }

    public onBlur($event: any): void {
        this.onCursorChange.emit({
            CursorPosition: this.getCursorPosition(),
            Source: this.source,
            EditorInstance: this.editorInstance
        });
        this.toggleEditorOverflow(false);
    }

    public onReady(editor: any): void {
        // subscribing language change for tooltip message
        this.lang$ = this.store.select(getCurrentLanguage);
        this.lang$.pipe(takeUntil(this.destroy$)).subscribe(lang => {
            const currentLanguage = this.translateService.use(lang);
            currentLanguage.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe(response => {
                this.updateAiButtonTooltip(editor);
            });
        });

        this.handleEnableDisableOfToolbarButton(editor, this.ipexAiUndo, false);
        this.toggleButtonVisibility(editor, this.ipexAiLoading, false);

        this.editorInstance = editor;
        editor.on('onAiButtonClick', () => {
            this.getIpexAiSpellCheck(editor);
        });

        editor.on(this.ipexAiUndo, () => {
            this.getIpexAiUndoText(editor);
        });

        editor.on(this.lineAndCharExceededEvent, (event: any, data: any) => {
            const message = data?.errorMessage ? 'APP_CREATE_CASE.' + data?.errorMessage : '';
            if (message) {
                // sometimes ckeditor throws multiple events for the same error
                // so we need to debounce the messages to prevent duplicate messages
                this.errorMessage$.next(message);
            }
        });

        editor.on('updateFormControlValidation', (event: any, data: any) => {
            this.updateFormControlValidation(data);
        });

        if (this.statLineCount) {
            editor.on('onCursorInfoChanged', (_: any, data: any) => {
                // if the total line count is different from the previous value, update the value
                // this prevent unnecessary updates to the form control
                if (data.totalLineCount !== this.statLineCount.value) {
                    this.statLineCount?.setValue(data.totalLineCount);
                }
            });
        }

        // Intercept pasted content at CKEditor model level:
        // Converts soft breaks <br> in pasted HTML to paragraphs <p> natively inside CKEditor.
        // This updates line counter & triggers validation errors WITHOUT causing cursor jumps
        const clipboardPipeline = editor.plugins.get('ClipboardPipeline');
        if (clipboardPipeline) {
            clipboardPipeline.on('inputTransformation', (evt: any, data: any) => {
                try {
                    const htmlString = editor.data.processor.toData(data.content);
                    if (htmlString && /<br\s*\/?>/gi.test(htmlString)) {
                        const fixedHtml = htmlString.replace(/<br\s*\/?>/gi, '</p><p>');
                        data.content = editor.data.processor.toView(fixedHtml);
                    }
                } catch (e) {
                    console.error('Error transforming pasted content in CKEditor:', e);
                }
            });
        }

        // Intercept Shift+Enter to create a new paragraph instead of soft break <br>
        const shiftEnterCmd = editor.commands.get('shiftEnter');
        if (shiftEnterCmd) {
            shiftEnterCmd.on('execute', (evt: any) => {
                evt.stop();
                editor.execute('enter');
            }, { priority: 'high' });
        }
    }

    private handleEnableDisableOfToolbarButton(editor: any, buttonName: string, isEnable: boolean): void {
        const toolbar = editor.ui.view.toolbar;
        const button = toolbar.items.find(item => item.label === buttonName);

        if (!button) {
            return;
        }

        button.isEnabled = isEnable;
    }

    private getIpexAiSpellCheck(editor: any): void {
        // blocking toolbar
        this.blockToolbarAndEditor(editor, true);

        // toggle ai and loading button
        this.toggleButtonVisibility(editor, this.ipexAiLoading, true);
        this.toggleButtonVisibility(editor, this.ipexAiButton, false);

        const currentData = editor.getData();
        const payload = {
            Content: currentData,
            QuestionId: this.questionId,
            CaseId: this.caseId,
            ActionType: SCConfig.IpexSpellCheckerActionType.spellCheck
        };

        this.spellCheckerService
            .getCorrectedText(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: response => {
                    if (response.StatusCode === 1) {
                        this.toggleButtonVisibility(editor, this.ipexAiLoading, false);
                        this.toggleButtonVisibility(editor, this.ipexAiButton, true);
                        this.toastMessageService.error(this.translateService.instant('SOMETHING_WENT_WRONG'));
                        this.blockToolbarAndEditor(editor, false);
                        return;
                    }

                    let aiCorrectedText = currentData;
                    let message = 'SOMETHING_WENT_WRONG';
                    if (response.StatusCode === 0) {
                        aiCorrectedText = response.Results?.CorrectedText ?? aiCorrectedText;
                        message = 'APP_CASES.DATA_PROVIDED_BY_AI';
                    }
                    this.toastMessageService.success(this.translateService.instant(message));

                    // By using editor.setData(aiCorrectedText), we will lose the undo history.
                    // This approach will keep the undo history intact.
                    editor.model.change(writer => {
                        // clearing the current content
                        const root = editor.model.document.getRoot();
                        writer.remove(writer.createRangeIn(root));

                        // inserting new content
                        const viewFragment = editor.data.processor.toView(aiCorrectedText);
                        const modelFragment = editor.data.toModel(viewFragment);
                        writer.insert(modelFragment, root, 'end');

                        // enabling ipex ai undo button
                        this.handleEnableDisableOfToolbarButton(editor, this.ipexAiUndo, true);
                        // disabling built in undo button
                        this.handleEnableDisableOfToolbarButton(editor, this.undo, false);

                        this.toggleButtonVisibility(editor, this.ipexAiLoading, false);
                        this.toggleButtonVisibility(editor, this.ipexAiButton, true);
                        this.blockToolbarAndEditor(editor, false);
                    });
                },
                error: errorResponse => {
                    console.error(errorResponse);
                    this.blockToolbarAndEditor(editor, false);
                    this.toggleButtonVisibility(editor, this.ipexAiLoading, false);
                    this.toggleButtonVisibility(editor, this.ipexAiButton, true);
                    this.toastMessageService.error(this.translateService.instant('SOMETHING_WENT_WRONG'));
                }
            });
    }

    private getIpexAiUndoText(editor: any): void {
        const currentData = editor.getData();
        const payload = {
            Content: currentData,
            QuestionId: this.questionId,
            CaseId: this.caseId,
            ActionType: SCConfig.IpexSpellCheckerActionType.oldText
        };

        this.spellCheckerService
            .getCorrectedText(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(response => {
                let aiCorrectedText = currentData;
                let message = 'SOMETHING_WENT_WRONG';
                if (response.StatusCode === 0) {
                    aiCorrectedText = response.Results.OriginalText;
                    message = 'APP_CASES.AI_TEXT_REMOVED';
                }

                this.toastMessageService.success(this.translateService.instant(message));

                editor.model.change(writer => {
                    const root = editor.model.document.getRoot();
                    writer.remove(writer.createRangeIn(root));

                    const viewFragment = editor.data.processor.toView(aiCorrectedText);
                    const modelFragment = editor.data.toModel(viewFragment);
                    writer.insert(modelFragment, root, 'end');

                    this.handleEnableDisableOfToolbarButton(editor, this.undo, true);
                    this.handleEnableDisableOfToolbarButton(editor, this.ipexAiUndo, false);
                });
            });
        if (this.isColorDisable) {
            editor.model.document.on('change:data', () => {
                editor.model.change(writer => {
                    const root = editor.model.document.getRoot();
                    const range = writer.createRangeIn(root);

                    for (const item of range.getItems()) {
                        if (item.hasAttribute('fontColor') || item.hasAttribute('fontBackgroundColor')) {
                            writer.removeAttribute('fontColor', item);
                            writer.removeAttribute('fontBackgroundColor', item);
                        }
                    }
                });
            });
        }
    }

    public getCursorPosition() {
        if (this.editorInstance) {
            const position = this.editorInstance.model.document.selection.getFirstPosition();
            return position;
        }
        return null;
    }

    private updateAiButtonTooltip(editor: any): void {
        const toolbarItems = editor.ui.view.toolbar.items;
        const aiButton = toolbarItems.find(item => item.label === this.ipexAiButton);
        let editorData = editor.getData();
        const plainText = editorData?.replace(/<[^>]*>?/gm, '');

        if (!aiButton) {
            return;
        }

        if (aiButton && plainText.length > this.allowedLength) {
            aiButton.tooltip = this.translateService
                .instant('APP_CASES.SPELL_CHECK_WARNING_MESSAGE')
                .replaceAll('WORD_COUNT', plainText.length);
            this.handleEnableDisableOfToolbarButton(editor, this.ipexAiButton, false);
        } else {
            aiButton.tooltip = '';
            this.handleEnableDisableOfToolbarButton(editor, this.ipexAiButton, true);
        }
    }

    private toggleButtonVisibility(editor: any, buttonName: string, isVisible: boolean) {
        const toolbarItems = editor.ui.view.toolbar.items;
        const button = toolbarItems.find(item => item.label === buttonName);

        if (button) {
            button.isVisible = isVisible;
        }
    }

    private blockToolbarAndEditor(editor: any, isDisabled: boolean): void {
        // sending event so that save button can enable/disable
        const currentValue = this.commonService.isSpellCheckerOnProcess$.value;

        this.commonService.isSpellCheckerOnProcess$.next(isDisabled ? currentValue + 1 : currentValue - 1);

        // Editor disable
        this.isReadOnly = isDisabled;

        const toolbarElement = editor.ui.view.toolbar.element;
        if (!toolbarElement) {
            return;
        }

        // we can directly hide the toolbar but only loader button need to show
        const buttonElements = toolbarElement.querySelectorAll('button');
        buttonElements.forEach(item => {
            if (item?.classList?.contains('ck-ipex-ai-loading-button')) {
                return;
            }
            item.disabled = isDisabled;
        });
    }

    private subscribeForRegulationConfigChanges(): void {
        this.commonService.$ckConfigurationChange
            .pipe(takeUntil(this.destroy$))
            .subscribe((configMap: Map<string, any>) => {
                const config = configMap.get(this.questionId);
                if (config) {
                    this.setConfigForLineAndCharIndicator(config);
                    this.updateEditorSize(config?.maxCharCount, config?.maxLineCount, config?.baseHeightLineCount);
                    const pluginConfigurationName = 'lineNumberAndCharCounterConfig';
                    this.editorInstance.config.set(pluginConfigurationName, this.lineAndCharConfig);

                    // Trigger a manual update by firing an event
                    // This will cause the plugin's updateText function to re-evaluate
                    this.editorInstance.fire('updateLineAndCharConfig');

                    // process done, reset the event
                    this.commonService.$ckConfigurationChange.next(new Map<string, any>()); // reset the event
                }
            });
    }

    private hasLineAndCharLimitConfig(): boolean {
        return this.lineAndCharConfig && JSON.stringify(this.lineAndCharConfig) !== '{}';
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();

        this.errorMessage$.complete();
    }
}
